/**
 * GeldVerdienenPage — Foto-Empfehlungsprogramm im Social Media Manager
 *
 * Verbindet zwei getrennt betriebene Anwendungen, OHNE sie technisch zu koppeln:
 * der Kunde meldet sich drüben in der Bewerbungsfoto-App als Empfehlungspartner
 * an, hinterlegt seinen Code hier, und bekommt daraus fertige Beiträge für seine
 * angebundenen Kanäle.
 *
 * Warum Deeplink statt Einbettung — alles gemessen, nicht vermutet:
 *   • iframe: die Bewerbungsfoto-App liefert `X-Frame-Options: SAMEORIGIN` und
 *     `Cross-Origin-Resource-Policy: same-origin` auf jeder Antwort. Eine CSP
 *     gibt es dort nicht (server.js:92 `contentSecurityPolicy: false`), also
 *     auch kein `frame-ancestors`, mit dem man uns gezielt erlauben könnte.
 *   • fetch: unser Origin steht nicht in ihrer CORS-Allowlist (server.js:240-264,
 *     kein Wildcard) — der Browser bekommt kein Access-Control-Allow-Origin.
 *   • Server-Proxy: `POST /api/affiliate/register` ist dort auf 3 Anmeldungen
 *     pro Stunde und IP begrenzt (rateLimit.js:101). Über einen Proxy kämen alle
 *     Kunden von EINER IP — der vierte pro Stunde liefe in einen 429.
 *
 * Der eigentliche Grund liegt aber tiefer: die Provision hängt an einem Cookie
 * auf der Bewerbungsfoto-Domain (AffiliateContext.jsx:10-19). Ein API-Aufruf von
 * hier aus könnte sie prinzipiell nicht zuordnen — der Kunde MUSS die Domain im
 * eigenen Browser öffnen. Deshalb ist der Link kein Notbehelf, sondern der
 * einzige Weg, der funktioniert.
 *
 * Backend-Endpunkte (beide mandantengetrennt):
 *   GET   /api/account/me            → liefert affiliate_code
 *   PATCH /api/account/affiliate-code → speichert/löscht ihn
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Coins, ExternalLink, Copy, Loader2, AlertTriangle,
  Info, Check, Gift, Link2, Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/**
 * Die EINE Stelle, an der die Adresse der Bewerbungsfoto-App steht.
 *
 * Bewusst eine Konstante und keine REACT_APP_-Variable: die Variablen werden zur
 * BAUZEIT eingebacken (frontend/Dockerfile:26-27), eine .env-Änderung würde also
 * ohne Rebuild wirkungslos verpuffen. Die Adresse ist zudem öffentlich, es gibt
 * nichts zu verbergen.
 *
 * `fotostudio.digital` ist als künftige Domain vorgesehen, existiert aber noch
 * nicht (Stand 14.08.2026: kein DNS-Eintrag, kein Caddy-Block). Sobald sie live
 * geht, wird hier eine Zeile getauscht.
 */
const AFFILIATE_BASE_URL = "https://bewerbungsfoto.foerderfuchs-bildung.de";
const AFFILIATE_SIGNUP_URL = `${AFFILIATE_BASE_URL}/geld-verdienen`;
const AFFILIATE_LOGIN_URL = `${AFFILIATE_BASE_URL}/affiliate/login`;

/** Format wie in api/affiliate/share-links.js:33 — ohne Schrägstrich vor dem ?. */
const empfehlungslink = (code) => `${AFFILIATE_BASE_URL}?ref=${code}`;

// ── Bausteine (Hausstil der BonusCenterPage) ─────────────────────────────────
function SectionCard({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function InfoBox({ children, variant = "blue" }) {
  const styles = {
    blue: "bg-sky-50 border-sky-200 text-sky-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    green: "bg-green-50 border-green-200 text-green-800",
  };
  return (
    <div className={`flex items-start gap-2.5 border rounded-xl px-4 py-3 text-sm ${styles[variant]}`}>
      <Info size={15} className="flex-shrink-0 mt-0.5" />
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

function KopierZeile({ text, label }) {
  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error("Kopieren nicht möglich — bitte den Text markieren.");
    }
  };
  return (
    <div className="flex items-start gap-2">
      <pre className="flex-1 whitespace-pre-wrap break-words bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-700 font-sans">
        {text}
      </pre>
      {/* aria-label statt nur "Kopieren": auf der Seite stehen vier dieser Knöpfe
          untereinander. Wer sie nur hört, bekäme sonst viermal dasselbe Wort und
          wüsste nicht, welchen Text er gerade kopiert. */}
      <button
        onClick={kopieren}
        aria-label={`${label} kopieren`}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-stone-200 text-sm text-stone-700 hover:bg-stone-100 flex-shrink-0"
        data-testid={`kopieren-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Copy size={15} /> Kopieren
      </button>
    </div>
  );
}

// ── Abschnitt 1: Wie es funktioniert ─────────────────────────────────────────
function ProgrammSektion() {
  return (
    <SectionCard>
      <div className="px-6 py-5 border-b border-stone-100 bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <Coins size={20} className="text-orange-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-stone-900 text-lg">Foto-Empfehlungsprogramm</h2>
            <p className="text-sm text-stone-500">Bewerbungsfotos empfehlen — an jedem Kauf mitverdienen</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { step: "1", text: "Einmalig als Empfehlungspartner anmelden", icon: ExternalLink },
            { step: "2", text: "Deinen Code hier unten hinterlegen", icon: Save },
            { step: "3", text: "Fertige Beiträge teilen und verdienen", icon: Coins },
          ].map(({ step, text, icon: Icon }) => (
            <div key={step} className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
              <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                {step}
              </div>
              <Icon size={14} className="text-stone-400 flex-shrink-0" />
              <span className="text-xs text-stone-600 leading-snug">{text}</span>
            </div>
          ))}
        </div>

        <InfoBox variant="amber">
          Die Anmeldung läuft in der Bewerbungsfoto-App und braucht eine{" "}
          <strong>eigene E-Mail-Adresse und ein eigenes Passwort</strong> — es ist ein
          getrenntes Konto, dein Zugang hier gilt dort nicht. Nach der Anmeldung
          bestätigst du deine E-Mail und findest den Code in deinem dortigen Dashboard.
        </InfoBox>

        <div className="flex flex-wrap items-center gap-2">
          <a
            href={AFFILIATE_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-fox-fire text-white text-sm font-medium hover:opacity-90"
            data-testid="affiliate-anmelden"
          >
            <ExternalLink size={15} /> Jetzt anmelden
          </a>
          <a
            href={AFFILIATE_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white border border-stone-200 text-sm text-stone-700 hover:bg-stone-100"
            data-testid="affiliate-dashboard"
          >
            <ExternalLink size={15} /> Zu meinem Foto-Dashboard
          </a>
        </div>
      </div>
    </SectionCard>
  );
}

// ── Abschnitt 2: Code hinterlegen + Beiträge ─────────────────────────────────
function CodeSektion({ konto, laedt, ladeFehler, onGespeichert }) {
  const gespeicherterCode = konto?.affiliate_code || null;
  const [code, setCode] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const [speicherFehler, setSpeicherFehler] = useState(null);

  // Das Eingabefeld folgt dem geladenen Stand — aber nur, wenn er sich wirklich
  // ändert. Sonst überschriebe ein Neu-Rendern die Eingabe des Kunden mitten im Tippen.
  useEffect(() => {
    setCode(gespeicherterCode || "");
  }, [gespeicherterCode]);

  const fehler = speicherFehler || ladeFehler;

  // Tippt der Kunde weiter, ist die alte Fehlermeldung überholt. Ohne das bliebe sie
  // stehen, bis er erneut speichert — und beim Zurücksetzen aufs Gespeicherte ist der
  // Knopf dann inaktiv, die rote Box also gar nicht mehr wegzubekommen.
  const codeAendern = (wert) => {
    setCode(wert);
    if (speicherFehler) setSpeicherFehler(null);
  };

  const speichern = async () => {
    setSpeichert(true);
    setSpeicherFehler(null);
    try {
      const res = await axios.patch(`${API}/account/affiliate-code`, {
        affiliate_code: code.trim(),
      });
      const neu = res.data?.affiliate_code || null;
      onGespeichert(neu);
      setCode(neu || "");
      toast.success(neu ? "Empfehlungscode gespeichert" : "Empfehlungscode entfernt");
    } catch (err) {
      // 422 = Formatfehler aus dem Pydantic-Validator. FastAPI verpackt die
      // Meldung in ein Array — die reine Zeichenkette wäre "[object Object]".
      const detail = err.response?.data?.detail;
      const text = Array.isArray(detail)
        ? detail.map((d) => d.msg?.replace(/^Value error, /, "")).join(" ")
        : detail;
      const meldung = text || "Speichern fehlgeschlagen. Bitte später erneut versuchen.";
      setSpeicherFehler(meldung);
      // Der Erfolgsfall meldet sich per Toast — der Fehlerfall muss das auch, sonst
      // ist Scheitern die leisere Rückmeldung von beiden.
      toast.error(meldung);
    } finally {
      setSpeichert(false);
    }
  };

  const link = gespeicherterCode ? empfehlungslink(gespeicherterCode) : null;
  const unveraendert = (code || "").trim() === (gespeicherterCode || "");

  return (
    <SectionCard>
      <div className="px-6 py-5 border-b border-stone-100 bg-gradient-to-r from-violet-50 to-indigo-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <Link2 size={20} className="text-violet-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-stone-900 text-lg">Dein Empfehlungscode</h2>
            <p className="text-sm text-stone-500">Einmal hinterlegen — wir bauen die Beiträge daraus</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-5">
        {laedt ? (
          <div className="space-y-3" data-testid="code-laedt">
            <div className="h-10 bg-stone-100 rounded-xl animate-pulse" />
            <div className="h-20 bg-stone-100 rounded-xl animate-pulse" />
          </div>
        ) : (
          <>
            {/* role="alert" ist hier kein Beiwerk: die Box wird nur neu in den DOM
                gehängt, außerhalb des Fokus. Ohne die Ankündigung hört ein blinder
                Kunde nach dem Speichern GAR NICHTS — weder Erfolg noch Fehler — und
                muss annehmen, sein Code sei hinterlegt. */}
            {fehler && (
              <div
                role="alert"
                className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                data-testid="code-fehler"
              >
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{fehler}</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <label htmlFor="affiliate-code" className="sr-only">
                Dein Empfehlungscode
              </label>
              <input
                id="affiliate-code"
                type="text"
                value={code}
                onChange={(e) => codeAendern(e.target.value)}
                placeholder="z. B. dozent_vorname_nachname_a1b2"
                aria-describedby="affiliate-code-hinweis"
                aria-invalid={Boolean(speicherFehler)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
                data-testid="code-eingabe"
              />
              <button
                onClick={speichern}
                disabled={speichert || unveraendert}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-fox-fire text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="code-speichern"
              >
                {speichert ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {speichert ? "Speichert…" : "Speichern"}
              </button>
            </div>
            <p id="affiliate-code-hinweis" className="text-xs text-stone-400">
              Den Code findest du in deinem Foto-Dashboard. Feld leeren und speichern
              entfernt ihn wieder.
            </p>

            {link && (
              <div className="space-y-4 pt-2" data-testid="beitraege">
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <Check size={15} className="flex-shrink-0" />
                  Code hinterlegt — deine Beiträge sind fertig.
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-stone-800">Dein Empfehlungslink</p>
                  <KopierZeile text={link} label="Link" />
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-stone-800">Fertige Beiträge</p>
                  <p className="text-xs text-stone-500">
                    Kopieren, in den Monatsplan einfügen oder direkt posten.
                  </p>
                  <KopierZeile
                    label="Beitrag kurz"
                    text={`Bewerbungsfoto gebraucht? Aus deinen eigenen Fotos wird ein professionelles Bewerbungsfoto — ohne Fotostudio, ohne Termin.\n\n${link}`}
                  />
                  <KopierZeile
                    label="Beitrag lang"
                    text={`Ein gutes Bewerbungsfoto entscheidet mit darüber, ob eine Bewerbung überhaupt gelesen wird. Nur: Studiotermin, Anfahrt, Wartezeit — für viele ist das die Hürde, an der es scheitert.\n\nEs geht auch anders: eigene Fotos hochladen, Paket wählen, fertiges Bewerbungsfoto erhalten.\n\n${link}`}
                  />
                  <KopierZeile
                    label="Nachricht"
                    text={`Hey, du suchst doch noch ein ordentliches Bewerbungsfoto — schau mal hier, das geht ohne Fotostudio: ${link}`}
                  />
                </div>

                {/* Diese drei Sätze müssen stimmen — sie sind die Grundlage, auf der
                    der Kunde Arbeit investiert. Belege in der Bewerbungsfoto-App:
                    • Last-Click: AffiliateContext.jsx:11-14 ruft setAffiliateCookie bei
                      JEDEM ?ref= auf, ohne auf ein bestehendes Cookie zu prüfen.
                    • Dauer: cookies.js:7-12 setzt rein clientseitig (js-cookie), damit
                      kappt Safari seit 13.1 auf 7 Tage — die 30 aus constants.js:397
                      gelten dort nicht.
                    • Klick nötig: die Zuordnung entsteht nur aus dem URL-Parameter. */}
                <InfoBox variant="blue">
                  Die Provision hängt am <strong>Klick auf deinen Link</strong> — ein
                  abgetippter Code zählt nicht. Die Zuordnung wird im Browser des
                  Interessenten gemerkt und hält <strong>bis zu 30 Tage</strong>; in Safari
                  und auf iPhones kürzer, weil diese Browser solche Merker auf 7 Tage
                  begrenzen. Klickt jemand später den Link einer anderen Person, zählt
                  deren Empfehlung.
                </InfoBox>
              </div>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}

// ── Hauptseite ───────────────────────────────────────────────────────────────
export default function GeldVerdienenPage() {
  const [konto, setKonto] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState(null);

  /**
   * @param still  true = ohne Ladeanzeige nachziehen. Nach dem Speichern soll die
   *               Sektion NICHT kurz in den Skeleton-Zustand zurückfallen — der
   *               Kunde hat gerade sein Ergebnis bekommen, das darf nicht flackern.
   */
  const laden = useCallback(async (still = false) => {
    if (!still) setLaedt(true);
    setLadeFehler(null);
    try {
      const res = await axios.get(`${API}/account/me`);
      setKonto(res.data || {});
    } catch (err) {
      // Beim stillen Nachziehen bewusst KEINE Fehlermeldung: das Speichern hat
      // funktioniert, der angezeigte Stand stimmt. Ein Fehler hier würde eine rote
      // Box über ein gelungenes Ergebnis setzen — genau der Widerspruch, den dieser
      // Nachzieher beseitigen soll.
      if (!still) {
        setLadeFehler(
          err.response?.data?.detail ||
            "Dein Empfehlungscode konnte nicht geladen werden. Bitte lade die Seite neu."
        );
      }
    } finally {
      if (!still) setLaedt(false);
    }
  }, []);

  useEffect(() => {
    laden();
  }, [laden]);

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-violet-500 rounded-xl flex items-center justify-center">
            <Coins size={20} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-stone-900">Geld verdienen</h1>
            <p className="text-sm text-stone-500">
              Empfiehl Bewerbungsfotos und verdiene an jedem Kauf mit.
            </p>
          </div>
        </div>
      </div>

      <ProgrammSektion />
      <CodeSektion
        konto={konto}
        laedt={laedt}
        ladeFehler={ladeFehler}
        onGespeichert={(neuerCode) => {
          // Sofort anzeigen, damit Link und Beiträge ohne Wartezeit erscheinen …
          setKonto((k) => ({ ...(k || {}), affiliate_code: neuerCode }));
          // … und dann den echten Serverstand nachziehen. Das räumt zugleich einen
          // stehengebliebenen ladeFehler weg: sonst stünde die rote Box "konnte nicht
          // geladen werden, bitte neu laden" direkt über der grünen Erfolgsmeldung,
          // und der Kunde wüsste nicht, welcher der beiden Aussagen er glauben soll.
          // Nebeneffekt: is_master wird nachgeholt, das nach einem gescheiterten
          // Erstabruf sonst dauerhaft undefined bliebe.
          laden(true);
        }}
      />

      {/* Abgrenzung zu /bonus — aber NUR für den Betreiber.
          /api/referral/my und /api/referral/generate-link hängen an require_master
          (server.py:5593, 5634), das hart auf is_owner prüft. Einem zahlenden Kunden
          diese Seite als zweite Einnahmequelle anzupreisen, würde ihn in eine rote
          403-Fehlerbox schicken — ein Versprechen, das die Software nicht hält.
          Fail-closed: solange konto null ist (lädt noch / Abruf gescheitert), wird
          der Hinweis nicht gezeigt. */}
      {konto?.is_master && (
        <SectionCard>
          <div className="px-6 py-5 flex items-start gap-3">
            <div className="w-9 h-9 bg-stone-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Gift size={17} className="text-stone-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-stone-800">
                Nicht verwechseln: Bonus &amp; Empfehlungen
              </p>
              <p className="text-sm text-stone-500 mt-0.5">
                Unter{" "}
                <a href="/bonus" className="underline hover:text-stone-700">
                  Bonus &amp; Empfehlungen
                </a>{" "}
                geht es um geworbene Kunden für den Social Media Manager selbst — ein
                Betreiber-Werkzeug. Diese Seite hier dreht sich um Bewerbungsfotos.
              </p>
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
